/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * useColumnConfiguration - Reusable hook for persisting DataGrid column configuration
 * (order, pinning, visibility) in localStorage.
 */

import { useCallback } from 'react'
import { useLocalStorage } from './useLocalStorage'

interface ColumnConfiguration {
  columnOrder: string[]
  columnPinning: { left: string[]; right: string[] }
  columnVisibility: Record<string, boolean>
}

interface UseColumnConfigurationOptions {
  storageKey: string
  defaultColumnOrder?: string[]
  defaultColumnPinning?: { left: string[]; right: string[] }
  defaultColumnVisibility?: Record<string, boolean>
}

export function useColumnConfiguration({
  storageKey,
  defaultColumnOrder = [],
  defaultColumnPinning = { left: [], right: [] },
  defaultColumnVisibility = {}
}: UseColumnConfigurationOptions) {
  const [config, setConfig] = useLocalStorage<ColumnConfiguration>(`columns-configuration_${storageKey}`, {
    columnOrder: defaultColumnOrder,
    columnPinning: defaultColumnPinning,
    columnVisibility: defaultColumnVisibility
  })

  const handleColumnOrderChange = useCallback(
    (newColumnOrder: string[]) => {
      setConfig((prev) => ({ ...prev, columnOrder: newColumnOrder }))
    },
    [setConfig]
  )

  const handleColumnVisibilityChange = useCallback(
    (newColumnVisibility: Record<string, boolean>) => {
      setConfig((prev) => ({ ...prev, columnVisibility: newColumnVisibility }))
    },
    [setConfig]
  )

  const handleColumnPinningChange = useCallback(
    (newColumnPinning: { left: string[]; right: string[] }) => {
      setConfig((prev) => ({ ...prev, columnPinning: newColumnPinning }))
    },
    [setConfig]
  )

  return {
    columnOrder: config.columnOrder,
    columnPinning: config.columnPinning,
    columnVisibility: config.columnVisibility,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    handleColumnPinningChange
  }
}
